import { Calendar, LayoutGrid, BookOpen, Users, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DashboardTabValue } from "./DashboardNavRail";

type Item = {
  value: DashboardTabValue;
  label: string;
  icon: typeof Calendar;
};

interface DashboardBottomTabsProps {
  activeTab: string;
  onChange: (value: DashboardTabValue) => void;
  terraceEnabled: boolean;
  className?: string;
}

/**
 * iOS-style bottom tab bar for the Dashboard (mobile, <768px).
 * - Fixed to viewport bottom, respects iOS safe-area inset.
 * - 5 icons + short labels, ≥44pt touch targets per Apple HIG.
 */
export function DashboardBottomTabs({
  activeTab,
  onChange,
  terraceEnabled,
  className,
}: DashboardBottomTabsProps) {
  const items: Item[] = [
    { value: "reservations", label: "Jour", icon: Calendar },
    { value: "salle", label: "Salle", icon: LayoutGrid },
    { value: "recherche", label: "Résas", icon: BookOpen },
    { value: "clients", label: "Clients", icon: Users },
    ...(terraceEnabled ? [{ value: "terrasse" as const, label: "Terr.", icon: Sun }] : []),
  ];

  return (
    <nav
      aria-label="Navigation principale"
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur",
        "pb-[env(safe-area-inset-bottom)]",
        className,
      )}
    >
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map(({ value, label, icon: Icon }) => {
          const active = activeTab === value;
          return (
            <li key={value}>
              <button
                type="button"
                onClick={() => onChange(value)}
                aria-current={active ? "page" : undefined}
                aria-label={label}
                className={cn(
                  "flex w-full flex-col items-center justify-center gap-0.5 h-14",
                  "text-[11px] font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className={cn("w-5 h-5", active && "scale-110 transition-transform")} />
                <span className="leading-none">{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
